import React, { useEffect, useState } from 'react'
import Fade from '@material-ui/core/Fade'
import Tooltip from '@material-ui/core/Tooltip'
import { Checkbox, FormControl, FormControlLabel, Grid } from '@material-ui/core'
import { StyledButton, colors } from '../../../theme/Global'
import { FilledInputStyle, LabelStyle, SmartHelper, StyledSwitch } from '../../../theme/Forms'
import { Loading, NotificationsSettings, PaddedBarWrapper, PaddedLabelStyle, SaveWrapper, StyledContainer, StyledForm } from './styles'
import Toast from '../../../common/components/Toast/Toast'
import userPreferencesService from '../../../services/userPreferencesService'
import useSessionStorage from '../../../hooks/useSessionStorage'

const MinimumWagePreferencesComponent = (props) => {
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState({})
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [showFailureToast, setShowFailureToast] = useState(false)
  const [email, setEmail] = useState('')
  const [frequency, setFrequency] = useState([])
  const [disabledNotifications, setDisabledNotifications] = useState(false)
  const [matchedCompany, setMatchedCompany] = useState({})
  const { setToSessionStorage, getFromSessionStorage } = useSessionStorage()

  // todo: refactor toasts - ELM-2139
  const successList = [
    {
      id: 1,
      title: 'Success',
      description: 'Your preferences were successfully updated',
      borderColor: colors.green
    }
  ]

  const errorList = [
    {
      id: 2,
      title: 'Error',
      description: 'There was an error in updating your preferences',
      borderColor: colors.govDocsRed
    }
  ]

  useEffect( () => {
    let subscribed = true
    setLoading(true)
    getSessionData().then( ({ userData, companyData }) => {
      if (subscribed) {
        setUser(userData)
        assignUserDataToState(userData, companyData)
        setLoading(false)
      }
    })
    return () => { subscribed = false }
  }, [])

  const getSessionData = async () => {
    const userData = await getFromSessionStorage('user')
    const companyData = await getFromSessionStorage('company')
    return { userData, companyData }
  }

  const assignUserDataToState = (userData, companyData) => {
    const currentCompany = userData.companies.find((userCompany) => userCompany.id === companyData._id)
    if (currentCompany
      && currentCompany.minWagePreferences
      && currentCompany.minWagePreferences.notifications
      && currentCompany.minWagePreferences.notifications.email
      && currentCompany.minWagePreferences.notifications.email.daysBeforeChange
      && currentCompany.minWagePreferences.notifications.email.daysBeforeChange.length === 0) {
      setDisabledNotifications(true)
    }
    setEmail(userData.email)
    setMatchedCompany(currentCompany)
    setFrequency((currentCompany
      && currentCompany.minWagePreferences
      && currentCompany.minWagePreferences.notifications
      && currentCompany.minWagePreferences.notifications.email
      && currentCompany.minWagePreferences.notifications.email.daysBeforeChange
    ) || [30])
  }

  const handleChangeFrequency = (event) => {
    let frequencies = [...frequency]
    const currentFrequency = +event.target.value
    const checked = event.target.checked

    if (checked) {
      if (frequencies.includes(30)) {
        frequencies.push(currentFrequency)
      } else {
        frequencies.push(currentFrequency, 30)
      }
      setDisabledNotifications(false)
    } else {
      frequencies = frequencies.filter((freq) => freq !== currentFrequency)
    }
    setFrequency(frequencies)
  }

  const handleChangeDisabledNotifications = (event) => {
    setDisabledNotifications(event.target.checked)
    setFrequency(event.target.checked ? [] : [30])
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      const adjustedCompanies = buildCompaniesWithMWPrefs()
      await userPreferencesService.putUserPreferences({ adjustedCompanies, user })
      await setToSessionStorage('user', { ...user, companies: adjustedCompanies })
      props.setUserNotificationPreferencesDispatch(adjustedCompanies) // update redux state
      setShowSuccessToast(true)
      setTimeout( () => { setShowSuccessToast(false) }, 3000)
    } catch (serviceError) {
      console.error(serviceError)
      setShowFailureToast(true)
      setTimeout( () => { setShowFailureToast(false) }, 3000)
    } finally {
      setLoading(false)
    }
  }

  const notificationsDisabledForAllCompanies = (companies) => {
    const disabledCompanies = companies.filter((company) => company.minWagePreferences && company.minWagePreferences.notifications
      && company.minWagePreferences.notifications.email && company.minWagePreferences.notifications.email.daysBeforeChange
      && company.minWagePreferences.notifications.email.daysBeforeChange.toString() === '' )

    return disabledCompanies.length === companies.length
  }

  // Note: This format is different than what is stored in the database. The put-user lambda maps it to a different format.
  const buildCompaniesWithMWPrefs = () => {
    let updatedCompanies = []

    if (disabledNotifications || notificationsDisabledForAllCompanies(user.companies)) {
      // when disabling or re-enabling, the settings impact ALL companies
      const newSettings = (disabledNotifications) ? { daysBeforeChange: [] } : { daysBeforeChange: frequency.map(Number) }

      user.companies.forEach((company) => {
        updatedCompanies.push({ ...company, minWagePreferences: { notifications: { email: newSettings } } })
      })
    } else {
      const filteredCompanies = user.companies.filter((companyToCheck) => companyToCheck.id !== matchedCompany.id)
      const companyWithMWPrefs = {
        ...matchedCompany,
        minWagePreferences: { notifications: { email: { daysBeforeChange: frequency.map(Number) } } }
      }
      updatedCompanies = [...filteredCompanies, companyWithMWPrefs]
    }

    return updatedCompanies
  }

  return (
    <StyledContainer>
      <NotificationsSettings variant="display1" align="left">
        Email Notification Settings
      </NotificationsSettings>
      <StyledForm>
        <FormControl fullWidth>
          <Grid container spacing={8}>
            <Grid container spacing={24} alignContent="flex-start">
              <Grid item xs={12} sm={6}>
                <LabelStyle htmlFor="email" data-testid="email-label">
                  Email to use for notifications
                </LabelStyle>
                <FilledInputStyle
                  helperText={<SmartHelper>Please contact your account manager to change your email address</SmartHelper>}
                  name="email"
                  type="text"
                  variant="filled"
                  value={email}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Grid item xs={12} sm={12}>
                  <LabelStyle htmlFor="frequency">Frequency</LabelStyle>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        data-testid="frequency-90"
                        disabled={loading || disabledNotifications}
                        checked={frequency.includes('90') || frequency.includes(90)}
                        onChange={handleChangeFrequency}
                        value="90"
                      />
                    )}
                    label="90 Days"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        data-testid="frequency-60"
                        disabled={loading || disabledNotifications}
                        checked={frequency.includes('60') || frequency.includes(60)}
                        onChange={handleChangeFrequency}
                        value="60"
                      />
                    )}
                    label="60 Days"
                  />
                  <Tooltip
                    TransitionComponent={Fade}
                    placement="right"
                    title="To be notified of recent wage changes, all users will receive a 30 day notification."
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          data-testid="frequency-monthly"
                          disabled
                          checked={frequency.includes('30') || frequency.includes(30)}
                          value="30"
                        />
                      )}
                      label="30 Days"
                    />
                  </Tooltip>
                </Grid>
              </Grid>

              <PaddedBarWrapper item xs={12} sm={12}>
                <Grid container spacing={8}>
                  <Grid item xs={12} sm={6}>
                    <PaddedLabelStyle>
                      Disable all email notifications to your email listed above
                    </PaddedLabelStyle>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledSwitch
                      showValue
                      defaultChecked={disabledNotifications}
                      disableRipple
                      checked={disabledNotifications}
                      onChange={handleChangeDisabledNotifications}
                      disabled={loading}
                    />
                  </Grid>
                </Grid>
              </PaddedBarWrapper>
            </Grid>
          </Grid>
          <SaveWrapper item xs={12} sm={12}>
            <StyledButton
              disabled={loading}
              color="primary"
              variant="contained"
              onClick={handleSave}
            >
              Save
            </StyledButton>
            {loading && <Loading size={24} />}
          </SaveWrapper>
        </FormControl>
      </StyledForm>
      {/* todo: refactor toasts - ELM-2139 */}
      { showSuccessToast ? (
        <Toast
          toastList={successList}
        />
      ) : null }
      { showFailureToast ? (
        <Toast
          toastList={errorList}
        />
      ) : null }
    </StyledContainer>
  )
}

export default MinimumWagePreferencesComponent
